import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {
  RecordData,
  RecordDataValue,
  RecordsDataRequestQueryParams,
  RecordTableColumnParams,
  RecordTableParams,
  RecordTotalData,
  RecordUpdateRequestParams
} from '../types/records-table.types';
import {ResourceParams, resources} from "../models/resource/resources";
import {RecordTableApiService} from "../services-api/record-table-api.service";
import {Pageable} from "../types/pagable.types";
import {UserPreferencesService} from "./user-preferences.service";
import {UserPreferences} from "../types/user-preferences.types";
import {DEFAULT_COLUMN_WIDTH} from "../constants/constants";
import {RecordTableColumnType} from "../types/record-table-column.types";
import {toggleGroupActionButtons} from "../components/records-table-toolbar/configs/records-table-toolbar-config";
import {UpdateRecordsTableActionsParams} from "../types/table-types/records-table-container.types";
import {dropdownSettingsResources} from "../models/resource/tables/dropdown-settings-resource-type";
import {bloggerModalResources} from "../models/resource/tables/blogger-modal-resource-type";
import {productCardModalTableResources} from "../models/resource/tables/product-card-resource-types";

@Injectable({
  providedIn: 'root'
})
export class RecordsTableService {
  private _currentResourceType: BehaviorSubject<ResourceParams> = new BehaviorSubject<ResourceParams>( resources['record-lists']);
  public currentResourceType$: Observable<ResourceParams> = this._currentResourceType.asObservable();

  private recordTableApiService: RecordTableApiService = inject(RecordTableApiService);
  private userPreferencesService: UserPreferencesService = inject(UserPreferencesService);

  public isColumnDisplayed(columnParams: RecordTableColumnParams): boolean {
    if (columnParams.hiddenGroup && !columnParams.sticky) { return false; }

    return true;
  }

  public getCurrentResourceType(): ResourceParams {
    return this._currentResourceType.getValue();
  }

  public setCurrentResourceType(resourceType: ResourceParams): void {
    this._currentResourceType.next(resourceType);
  }

  public getDefaultValueAccordingToType(columnType: RecordTableColumnType): RecordDataValue {
    switch (columnType) {
      case 'TEXT': return '';
      case 'NUMBER':
      case 'NUMERIC':
      case 'DATE':
      case 'MODAL':
      case 'IFRAME':
      case 'COLOR':
      case 'DATE_YEAR':
      case 'DATE_MONTH_YEAR':
      case 'TIME':
      case 'SELECT': return null;
      case 'BOOLEAN': return false;
      default: return null;
    }
  }

  public getColumnWidth(columnParam: RecordTableColumnParams, tableId?: string): number {
    const userPreferences: UserPreferences | null = this.userPreferencesService.getUserPreferences();
    if (userPreferences?.resizeParams && tableId) {
      const compositeKey = `${tableId}_${columnParam.key}`;
      const resizeParam = userPreferences.resizeParams?.find(i => i.key === compositeKey);
      if (resizeParam) {
        return resizeParam.width!;
      }
    }

    if (userPreferences?.resizeParams) {
      const resizeParam = userPreferences.resizeParams?.find(i => i.key === columnParam.key);
      if (resizeParam) {
        return resizeParam.width!;
      }
    }

    return this.getDefaultMinWidthColumn(columnParam);
  }

  public getDefaultMinWidthColumn(columnParam: RecordTableColumnParams): number {
    return columnParam.columnProperties.WIDTH?.value ?
      columnParam.columnProperties.WIDTH.value :
      DEFAULT_COLUMN_WIDTH;
  }

  public updateRecordsTableActions(updateRecordsTableActionsParams: UpdateRecordsTableActionsParams) {
    const { recordTableParamsKey, recordsTableActions, recordsTableColumnsParams, resourceParamsPath } = updateRecordsTableActionsParams;
    const isDropdownSettings = Object.keys(dropdownSettingsResources).includes(resourceParamsPath);
    const isBloggerModalTable = Object.keys(bloggerModalResources).includes(resourceParamsPath);
    const isProductCardModalTable = Object.keys(productCardModalTableResources).includes(resourceParamsPath);
    const needSettings = recordTableParamsKey && !isDropdownSettings && !isBloggerModalTable && !isProductCardModalTable;
    const needResize = recordTableParamsKey && !isDropdownSettings;

    needResize && recordsTableActions.push('RESIZE_COLUMN');
    needSettings && recordsTableActions.push('SETTINGS_TABLE');
    recordsTableColumnsParams.some(i => i.columnProperties.GROUP) &&
      recordsTableActions.push(...toggleGroupActionButtons.map(i => i.action));

    return recordsTableActions;
  }

  public getRecordTableColumnsParams(path: string): Promise<RecordTableParams> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.getRecordTableParams(path).subscribe({
        next: (tableParams: RecordTableParams) => resolve(tableParams),
        error: (error) => reject(error)
      });
    });
  }

  public getRecordTableTotals(path: string, queryParams: RecordsDataRequestQueryParams): Promise<Array<RecordTotalData>> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.getRecordTableTotals(path, queryParams).subscribe({
        next: (recordsTableData: Array<RecordTotalData>) => resolve(recordsTableData),
        error: (error) => reject(error)
      });
    });
  }

  public getRecordData(path: string, queryParams: RecordsDataRequestQueryParams): Promise<Pageable<RecordData>> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.fetchRecordTableData(path, queryParams).subscribe({
        next: (recordsTableData: Pageable<RecordData>) => resolve(recordsTableData),
        error: (error) => reject(error)
      });
    });
  }

  public updatedRecordField<T>(recordUpdateRequestParams: RecordUpdateRequestParams): Promise<T> {
    return new Promise((resolve, reject) => {
      this.recordTableApiService.updateRecord<T>(recordUpdateRequestParams).subscribe({
        next: (recordPropertyData: T) => resolve(recordPropertyData),
        error: (error) => reject(error)
      });
    });
  }
}
